import {
  ContentfulCartProduct,
  ContentfulPurchasedPrintedDesign,
  ContentfulPurchasedProduct,
} from "@cms/types"
import { CartData, CartLineItem } from "@shopify/types"
import { getGeo } from "@utils/api"
import { getCookie, getIn } from "@utils/index"
import * as R from "ramda"

import {
  CartItem,
  CartProducts,
  LocalCheckout,
  PurchasedProducts,
} from "./types"

export const graphQLRequest = async (fn, options) => {
  try {
    const { data, errors } = await fn(options)
    return { data, errors }
  } catch (error) {
    return { data: null, errors: error }
  }
}

export const UNKNOWN_LOCATION = "UNKNOWN_LOCATION"

export const fetchCustomerLocation = async () => {
  const cookieCountryCode = getCookie("userCountryCode")
  const cookieStateCode = getCookie("userRegionCode")
  const cookieZipCode = getCookie("userPostalCode")

  if (cookieCountryCode && cookieStateCode && cookieZipCode) {
    return {
      customerCountryCode: cookieCountryCode,
      customerStateCode: cookieStateCode,
      customerZipCode: cookieZipCode,
    }
  }

  let data

  try {
    data = await getGeo()
  } catch (error) {
    console.error(error)
  }

  return {
    customerCountryCode: getIn(data, "geo.country.code", UNKNOWN_LOCATION),
    customerStateCode: getIn(data, "geo.subdivision.code", UNKNOWN_LOCATION),
    customerZipCode: getIn(data, "geo.postal_code", UNKNOWN_LOCATION),
  }
}

export const getMissingCartProductIds = (
  checkoutData: CartData,
  cartProducts: CartProducts
) => {
  const lines = getIn(checkoutData, "lines.edges", []) as Array<{
    node: CartLineItem
  }>

  const ids: Array<string> = []

  lines.forEach(({ node: line }) => {
    const sku = line.merchandise.sku

    if (!cartProducts[sku]) {
      const contentfulVariantId = line.attributes.find(
        ({ key }) => key === "_contentfulVariantId"
      )

      if (contentfulVariantId) {
        ids.push(contentfulVariantId?.value)
      }
    }
  })

  return ids
}

// shopifyProductId = Encoded
// shopifyVariantId = gid://shopify/ProductVariant/someId
export const formatContentfulCartProductsData = (contentfulData) => {
  const products: Array<ContentfulCartProduct> = getIn(
    contentfulData,
    "productVariantCollection.items",
    []
  )

  return products.reduce((cartProducts, product) => {
    let multipleDevices
    const productDevices = getIn(
      product,
      "linkedFrom.productCollection.items.0.devices",
      []
    )
    if (productDevices.length > 0) {
      multipleDevices = productDevices.reduce((acc, curr) => {
        if (acc) {
          acc = acc + `, ${curr.name}`
        } else {
          acc = curr.name
        }

        return acc
      }, undefined)
    }

    const cartProduct = {
      brand: getIn(product, "linkedFrom.productCollection.items.0.brand.name"),
      builtInMagnet: getIn(
        product,
        "linkedFrom.productCollection.items.0.builtInMagnet",
        false
      ),
      bundleProduct: getIn(
        product,
        "linkedFrom.productCollection.items.0.bundleProduct"
      ),
      color: getIn(product, "color.name"),
      compareAtPrice: getIn(product, "compareAtPrice"),
      contentfulProductId: getIn(
        product,
        "linkedFrom.productCollection.items.0.sys.id"
      ),
      contentfulVariantId: getIn(product, "sys.id"),
      device: multipleDevices
        ? multipleDevices
        : getIn(product, "linkedFrom.productCollection.items.0.device.name"),
      deviceType: getIn(
        product,
        "linkedFrom.productCollection.items.0.device.type.name"
      ),
      image: getIn(product, "imagesCollection.items.0.url"),
      marketPrices: getIn(product, "marketPrices", {}),
      price: getIn(product, "price"),
      productName: getIn(
        product,
        "linkedFrom.productCollection.items.0.productName"
      ),
      productSlug: getIn(product, "linkedFrom.productCollection.items.0.slug"),
      productSubType: getIn(
        product,
        "linkedFrom.productCollection.items.0.productSubType.slug"
      ),
      shopifyProductId: getIn(
        product,
        "linkedFrom.productCollection.items.0.shopifyProduct.shopifyProductId"
      ),
      shopifyVariantId: getIn(product, "shopifyVariantId"),
      size: getIn(product, "size.size"),
      sku: getIn(product, "sku"),
      variantName: getIn(product, "name"),
    }

    cartProducts[cartProduct.sku] = cartProduct
    return cartProducts
  }, {}) as CartProducts
}

export const getLocalCheckoutProps = (cartData: CartData) => ({
  currencyCode: getIn(cartData, "cost.totalAmount.currencyCode"),
  duty: getIn(cartData, "cost.totalDutyAmount.amount"),
  id: getIn(cartData, "id"),
  subtotal: getIn(cartData, "cost.subtotalAmount.amount"),
  tax: getIn(cartData, "cost.totalTaxAmount.amount"),
  total: getIn(cartData, "cost.totalAmount.amount"),
})

export const buildLocalCheckout = (
  cartData: CartData,
  cartProducts: CartProducts
) => {
  const lines = getIn(cartData, "lines.edges", []) as Array<{
    node: CartLineItem
  }>
  const localCheckoutItems = R.pipe(
    R.map(({ node: line }: any) => {
      const sku = line.merchandise.sku
      const cartProduct = cartProducts[sku]

      if (!cartProduct) {
        return undefined
      }

      return {
        ...cartProduct,
        cost: getIn(line, "cost", {}),
        discountAllocations: getIn(line, "discountAllocations", []),
        quantity: line.quantity,
      }
    }),
    R.filter((item: any) => !!item)
  )(lines)

  return {
    ...getLocalCheckoutProps(cartData),
    items: localCheckoutItems,
  } as LocalCheckout
}

// cartItem.shopifyVariantId = gid://shopify/ProductVariant/someId
export const createCheckoutLineItem = (cartItem: CartItem) => {
  const attributes = [
    { key: "_contentfulProductId", value: cartItem.contentfulProductId },
    { key: "_contentfulVariantId", value: cartItem.contentfulVariantId },
    { key: "_productSlug", value: cartItem.productSlug },
    {
      key: "_addedAt",
      value: new Date().toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
      }),
    },
  ]

  return {
    attributes,
    merchandiseId: btoa(cartItem.shopifyVariantId),
    quantity: cartItem.quantity,
  }
}

export const buildPurchasedProducts = (
  data: Array<ContentfulPurchasedPrintedDesign | ContentfulPurchasedProduct>
) => {
  const purchasedProducts = data.reduce((acc, curr) => {
    if (curr.__typename === "ContentfulPrintedDesignVariant") {
      const combinedSku = `${getIn(
        curr,
        "linkedFrom.printedDesignCollection.items.0.baseSku",
        ""
      )} + ${getIn(curr, "sku", "")}`

      acc[combinedSku] = curr
    } else {
      acc[curr.sku] = curr
    }

    return acc
  }, {}) as PurchasedProducts
  return purchasedProducts
}
